import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Stack, TextField, Typography } from '@mui/material';
import { createIntake, getIntakeByRequest, getAccounts, getCategories, getSplitTemplates, getTallyActivities, updateIntake } from '../../services/api';
import { fundingIds, intakeChanged, localToday, newRequestKey, ownerScope, requestStorageKey, transactionsChanged } from '../../utils/intake';
import FundingPicker from './FundingPicker';
import ReceiptViewer from './ReceiptViewer';

async function fileHash(file) {
  if (!file) return null;
  const bytes = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
}

export default function IntakeEntryDialog({ open, onClose, onSaved, defaultDestination = 'tally', initialFunding, entry }) {
  const [destination, setDestination] = useState(defaultDestination);
  const [nature, setNature] = useState('expense');
  const [funding, setFunding] = useState(fundingIds());
  const [accounts, setAccounts] = useState([]), [categories, setCategories] = useState([]), [templates, setTemplates] = useState([]);
  const [activities, setActivities] = useState([]), [activity, setActivity] = useState('');
  const [amount, setAmount] = useState(''), [date, setDate] = useState(localToday()), [description, setDescription] = useState('');
  const [dirty, setDirty] = useState({}), [file, setFile] = useState(null), [analyze, setAnalyze] = useState(true);
  const [busy, setBusy] = useState(false), [loaded, setLoaded] = useState(false), [error, setError] = useState('');
  const [pending, setPending] = useState(null), [viewFile, setViewFile] = useState(false);
  const automatic = !!file && analyze && !entry;
  useEffect(() => {
    if (!open) { setViewFile(false); return; }
    let alive = true;
    setError(''); setLoaded(false); setFile(null); setDirty({}); setAnalyze(true); setActivity('');
    setDestination(entry?.destination || defaultDestination); setNature(entry?.transaction_nature || 'expense');
    setFunding(fundingIds(entry?.funding || initialFunding)); setAmount(entry?.amount || '');
    setDate(entry?.transaction_date || localToday()); setDescription(entry?.description || '');
    try { setPending(entry ? null : JSON.parse(sessionStorage.getItem(requestStorageKey('upload')) || 'null')); }
    catch { setPending(null); }
    Promise.all([getAccounts(), getCategories(), getSplitTemplates(), getTallyActivities()]).then(([a, c, t, p]) => {
      if (alive) { setAccounts(a.data); setCategories(c.data); setTemplates(t.data); setActivities(p.data); setLoaded(true); }
    }).catch(() => { if (alive) setError('Could not load funding choices. Close and reopen to retry.'); });
    return () => { alive = false; };
    // Initial choices are copied only when opening; later parent renders must not reset owner edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry]);

  const accepted = (saved, scope, storageKey) => {
    if (storageKey) sessionStorage.removeItem(storageKey);
    if (scope !== ownerScope()) return;
    setPending(null); intakeChanged();
    if (saved.status === 'posted') transactionsChanged();
    onSaved?.(saved); onClose();
  };
  const reconcile = async () => {
    setBusy(true); setError('');
    const scope = ownerScope(), key = requestStorageKey('upload');
    try { const { data } = await getIntakeByRequest(pending.payload.request_key); accepted(data, scope, key); }
    catch (e) { setError(e.response?.status === 404
      ? 'Not accepted yet. Re-select the same original, if any, and retry the saved request. Its identifier will not change.'
      : 'Could not check the saved request. Retry checking; do not enter this purchase again.'); }
    finally { setBusy(false); }
  };
  const submit = async e => {
    e.preventDefault(); e.stopPropagation(); setBusy(true); setError('');
    const scope = ownerScope(), storageKey = requestStorageKey('upload');
    try {
      if (entry) {
        const { data } = await updateIntake(entry.id, { version: entry.version, funding,
          fields: { amount, transaction_date: date, description } });
        accepted(data, scope, null); return;
      }
      let savedRequest = pending;
      if (!savedRequest) {
        if (destination === 'direct' && !file) throw new Error('Attach a receipt, or use normal manual transaction entry.');
        const fields = {};
        if (!automatic || dirty.amount) fields.amount = amount;
        if (!automatic || dirty.date) fields.transaction_date = date;
        if (!automatic || dirty.description) fields.description = description;
        savedRequest = { payload: { request_key: newRequestKey(), destination, transaction_nature: nature,
          ...funding, analyze: automatic, fields, activity_label: activities.find(a => a.id === activity)?.name || '' }, file_hash: await fileHash(file), filename: file?.name };
        sessionStorage.setItem(storageKey, JSON.stringify(savedRequest));
        setPending(savedRequest);
      } else if (savedRequest.file_hash !== await fileHash(file)) {
        throw new Error(`Re-select the same original (${savedRequest.filename}) to retry safely.`);
      }
      const { data } = await createIntake(savedRequest.payload, file);
      accepted(data, scope, storageKey);
    } catch (err) {
      // A definite validation rejection did not publish. Network/5xx outcomes retain the exact request.
      if (err.response && err.response.status < 500 && err.response.status !== 409) {
        sessionStorage.removeItem(storageKey); setPending(null);
      }
      setError(err.response?.data?.msg || err.message || 'Could not confirm acceptance. Retry the same request.');
    } finally { setBusy(false); }
  };
  return <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>{entry ? 'Review transaction details' : 'Receipt or daily tally'}</DialogTitle>
    <DialogContent><Stack component="form" id="intake-entry-form" onSubmit={submit} spacing={2} sx={{ pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {pending && <Alert severity="warning">An earlier request needs reconciliation. Retry uses the same identifier, never a second deduction.
        <Button disabled={busy} onClick={reconcile}>Check acceptance</Button></Alert>}
      <fieldset disabled={busy || !!pending || !loaded} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {!entry && <TextField select fullWidth label="Save to" value={destination} onChange={e => { setDestination(e.target.value); setNature('expense'); }}>
          <MenuItem value="direct">Normal transaction — apply when ready</MenuItem>
          <MenuItem value="tally">Daily Tally — apply after card payment</MenuItem>
        </TextField>}
        {!entry && activities.length > 0 && <TextField select fullWidth margin="normal" label="Frequent activity (optional)" value={activity}
          onChange={e => { const p = activities.find(a => a.id === e.target.value); setActivity(p?.id || ''); if (p) setFunding(fundingIds(p.funding)); }}>
          <MenuItem value="">Custom transaction</MenuItem>{activities.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
        </TextField>}
        {destination === 'direct' && !entry && <TextField select fullWidth margin="normal" label="Transaction type" value={nature}
          onChange={e => { setNature(e.target.value); setFunding(f => ({ ...f, split_template_id: '' })); }}>
          <MenuItem value="expense">Expense</MenuItem><MenuItem value="income">Income</MenuItem>
        </TextField>}
        <FundingPicker value={funding} onChange={setFunding} accounts={accounts} categories={categories} templates={templates} nature={nature} />
        {!entry && <FormControlLabel control={<Checkbox checked={analyze} onChange={e => setAnalyze(e.target.checked)} />}
          label="Fill receipt details with AI in the background" />}
        <TextField fullWidth margin="normal" label="Total amount" type="number" value={amount} required={!automatic && !pending}
          onChange={e => { setAmount(e.target.value); setDirty(d => ({ ...d, amount: true })); }}
          inputProps={{ min: '0.01', step: '0.01' }} helperText={automatic ? 'Leave blank to read the final total, including tax and tip.' : 'Positive amount; no receipt itemization.'} />
        <TextField fullWidth margin="normal" label="Purchase date" type="date" InputLabelProps={{ shrink: true }} value={date}
          required={!automatic && !pending} onChange={e => { setDate(e.target.value); setDirty(d => ({ ...d, date: true })); }}
          helperText={automatic && !dirty.date ? 'The document date will be used, not the displayed default.' : ''} />
        <TextField fullWidth margin="normal" label="Merchant / description" value={description} inputProps={{ maxLength: 2000 }}
          onChange={e => { setDescription(e.target.value); setDirty(d => ({ ...d, description: true })); }} />
      </fieldset>
      {!entry && <Stack direction="row" gap={1} flexWrap="wrap">
        <Button component="label" variant="outlined" disabled={busy}>Choose image or PDF
          <input hidden type="file" aria-label="Receipt file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={e => {
            const chosen = e.target.files?.[0];
            if (chosen && chosen.size > 10 * 1024 * 1024) { setError('Choose an original no larger than 10 MiB.'); return; }
            setFile(chosen || null);
          }} /></Button>
        {file && <Button onClick={() => setViewFile(true)}>{file.name} — view</Button>}
        {file && !pending && <Button onClick={() => setFile(null)} disabled={busy}>Remove</Button>}
      </Stack>}
      {entry?.has_receipt && <Button onClick={() => setViewFile(true)}>View original</Button>}
      <Typography variant="body2" color="text.secondary">{destination === 'tally'
        ? 'No balance changes yet. Each purchase is applied only after you confirm its consolidated card payment.'
        : 'Usable details are applied automatically. Unclear receipts remain available for correction.'}
        {' '}Originals stay private and viewable; nothing is sent to recurring vendors or LLC compliance.</Typography>
    </Stack></DialogContent>
    <DialogActions><Button disabled={busy} onClick={onClose}>Close</Button>
      <Button type="submit" form="intake-entry-form" variant="contained" disabled={busy || !loaded}>
        {busy ? 'Saving…' : pending ? 'Retry saved request' : entry ? (destination === 'direct' ? 'Save and apply' : 'Save correction') : automatic ? 'Upload and continue' : destination === 'tally' ? 'Add to tally' : 'Save and apply'}
      </Button></DialogActions>
    <ReceiptViewer open={viewFile} onClose={() => setViewFile(false)} file={file} entryId={entry?.id} />
  </Dialog>;
}
